import {
    Box,
    Collapse,
    IconButton,
    Menu,
    MenuItem,
    MenuProps,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    useTheme,
} from '@mui/material';
import { SxProps } from '@mui/material/styles';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { colors } from '../theme/theme';
import CommonStatusChip from '../ui-components/StatusChipComponent';

import { TableContainerWrapper } from './CommonTableStyles';

type SortDirection = 'asc' | 'desc' | null;

interface Column {
    id: string;
    label: string;
    align?: 'left' | 'right' | 'center';
    sortable?: boolean;
    /** Optional min width for the column (e.g. 140) */
    minWidth?: number | string;
    /** Optional filter: when set, a filter icon is shown in the header; options are shown in a menu on click */
    filterOptions?: Array<{ value: string; label: string }>;
}

interface ActionItem {
    icon: string; // Icon class name (e.g., 'icon-download', 'icon-edit', 'icon-delete')
    onClick: (row: Record<string, unknown>, rowIndex: number) => void;
    disabled?: boolean;
    tooltip?: string;
    iconColor?: string;
    /** When false, the action is not rendered (e.g. hide preview when unsupported). */
    visible?: (row: Record<string, unknown>, rowIndex: number) => boolean;
}

interface CommonTableProps {
    columns: Column[];
    rows: Record<string, unknown>[];
    minWidth?: number;
    isPagination?: boolean;
    enableAccordion?: boolean;
    renderAccordionContent?: (row: Record<string, unknown>, rowIndex: number) => React.ReactNode;
    onPageChange?: (page: number, rowsPerPage: number) => void;
    totalCount?: number; // For server-side pagination
    serverSide?: boolean; // Enable server-side pagination mode
    page?: number; // Controlled page for server-side pagination (1-based)
    rowsPerPage?: number;
    onRowClick?: (row: Record<string, unknown>, rowIndex: number) => void;
    sx?: SxProps;
    isActions?: boolean; // Show Actions column
    actions?: ActionItem[]; // Array of action items (up to 3)
    actionsLabel?: string; // Label for Actions column (default: "Action")
    getRowSx?: (row: Record<string, unknown>, rowIndex: number) => SxProps;
    /** When 'support', status column uses support-request-specific colors (Open, In Progress, Closed, Completed). Omit or use 'default' for generic status (Completed=green, else blue). */
    statusVariant?: 'default' | 'support';
    stickyHeader?: boolean;
    /** Skip the shadow on the sticky actions column when the table scrolls horizontally. */
    hideActionsShadow?: boolean;
    maxHeight?: string | number | Record<string, string | number>;
    className?: string;
    tableBorder?: string;
    /** Optional: current filter value per column (for columns that have filterOptions) */
    columnFilterValues?: Record<string, string | null>;
    /** Optional: called when user selects a filter option; pass null to clear */
    onFilterChange?: (columnId: string, value: string | null) => void;
    /** Optional: render a full-width detail row below each data row; return null to show nothing */
    renderRowDetail?: (row: Record<string, unknown>, rowIndex: number) => React.ReactNode | null;
    /** Optional ref to the actual table scroll container for external scroll controls */
    scrollContainerRef?: React.Ref<HTMLDivElement>;
}
const CommonTable: React.FC<CommonTableProps> = ({
    columns,
    rows,
    minWidth = 650,
    isPagination = false,
    enableAccordion = false,
    renderAccordionContent,
    onPageChange,
    totalCount,
    serverSide = false,
    page: controlledPage,
    rowsPerPage: controlledRowsPerPage,
    sx,
    isActions = false,
    actions = [],
    actionsLabel = 'Action',
    getRowSx,
    statusVariant = 'default',
    stickyHeader = false,
    hideActionsShadow = false,
    maxHeight,
    className,
    tableBorder = `1px solid ${colors.greyPreset[20]}`,
    columnFilterValues = {},
    onFilterChange,
    renderRowDetail,
    scrollContainerRef,
}) => {
    const theme = useTheme();

    const [sortColumn, setSortColumn] = useState<string | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [internalPage, setInternalPage] = useState(0);
    const [internalRowsPerPage, setInternalRowsPerPage] = useState(10);
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
    const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
    const [filterColumnId, setFilterColumnId] = useState<string | null>(null);
    const [isHorizontallyScrollable, setIsHorizontallyScrollable] = useState(false);
    const internalScrollRef = useRef<HTMLDivElement | null>(null);

    const setScrollContainerRefs = useCallback(
        (node: HTMLDivElement | null) => {
            internalScrollRef.current = node;
            if (typeof scrollContainerRef === 'function') {
                scrollContainerRef(node);
            } else if (scrollContainerRef && 'current' in scrollContainerRef) {
                (scrollContainerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
            }
        },
        [scrollContainerRef],
    );

    // Use a very large number to represent "All" instead of -1
    const ALL_ROWS_VALUE = 999999;

    // Use controlled values for server-side, internal state for client-side
    const page = serverSide && controlledPage !== undefined ? controlledPage - 1 : internalPage; // Convert 1-based to 0-based
    const rowsPerPage = serverSide && controlledRowsPerPage !== undefined ? controlledRowsPerPage : internalRowsPerPage;

    const handleSort = (columnId: string) => {
        if (sortColumn === columnId) {
            // Toggle direction: asc -> desc -> asc (keep sorting, just toggle direction)
            if (sortDirection === 'asc') {
                setSortDirection('desc');
            } else if (sortDirection === 'desc') {
                setSortDirection('asc');
            }
        } else {
            setSortColumn(columnId);
            setSortDirection('asc');
        }
    };

    const sortedRows = useMemo(() => {
        if (!sortColumn || !sortDirection) {
            return rows;
        }

        return [...rows].sort((a, b) => {
            const aValue: unknown = a[sortColumn];
            const bValue: unknown = b[sortColumn];

            // Handle null/undefined values
            if (aValue == null && bValue == null) return 0;
            if (aValue == null) return 1;
            if (bValue == null) return -1;

            // Handle date sorting (format: "Oct 5, 2025" or "01/15/2024" or ISO "2025-12-14")
            if (sortColumn === 'date' || sortColumn === 'paymentDate' || sortColumn === 'dueDate') {
                // Check if ISO version of date field exists for reliable sorting
                const isoFieldName = `${sortColumn}ISO`;
                const aISOValue = a[isoFieldName];
                const bISOValue = b[isoFieldName];

                // Use ISO dates if available (more reliable), otherwise parse formatted dates
                const aValueForSort = (aISOValue || aValue) as string;
                const bValueForSort = (bISOValue || bValue) as string;

                const parseDate = (dateStr: string): Date => {
                    return new Date(dateStr);
                };
                const aDate = parseDate(aValueForSort);
                const bDate = parseDate(bValueForSort);
                if (sortDirection === 'asc') {
                    return aDate.getTime() - bDate.getTime();
                } else {
                    return bDate.getTime() - aDate.getTime();
                }
            }

            // Handle status sorting with specific order
            if (sortColumn === 'status') {
                const statusOrder: Record<string, number> = {
                    Open: 1,
                    'In Progress': 2,
                    Closed: 3,
                    Completed: 4,
                };
                const aOrder = statusOrder[String(aValue)] || 999;
                const bOrder = statusOrder[String(bValue)] || 999;
                if (sortDirection === 'asc') {
                    return aOrder - bOrder;
                } else {
                    return bOrder - aOrder;
                }
            }

            // Handle amount sorting (currency format like "$120", "$450")
            if (sortColumn === 'amount') {
                const parseAmount = (amountStr: string): number => {
                    // Remove currency symbols and commas, then parse as float
                    const cleaned = String(amountStr).replace(/[^0-9.-]/g, '');
                    return parseFloat(cleaned) || 0;
                };
                const aAmount = parseAmount(String(aValue));
                const bAmount = parseAmount(String(bValue));
                if (sortDirection === 'asc') {
                    return aAmount - bAmount;
                } else {
                    return bAmount - aAmount;
                }
            }

            // Handle size sorting (file size format like "123.45 KB", "1.23 MB", "500 B", "N/A")
            if (sortColumn === 'size') {
                const parseSize = (sizeStr: string | number): number => {
                    // Handle null/undefined
                    if (sizeStr == null) return -1; // Put null/undefined at the end

                    // If it's already a number (bytes), return it
                    if (typeof sizeStr === 'number') {
                        return sizeStr;
                    }

                    // Handle string values
                    const str = String(sizeStr).trim();

                    // Handle "N/A", empty strings, or invalid values
                    if (!str || str === 'N/A' || str === 'n/a' || str === '') {
                        return -1; // Put invalid values at the end
                    }

                    // Extract number and unit from strings like "123.45 KB", "1.23 MB", "500 B"
                    const match = str.match(/^([\d.]+)\s*(KB|MB|GB|B|bytes?)?$/i);
                    if (!match) return -1; // Invalid format, put at the end

                    const numericValue = parseFloat(match[1]);
                    if (isNaN(numericValue)) return -1;

                    const unit = (match[2] || 'KB').toUpperCase(); // Default to KB if no unit specified

                    // Convert to bytes for consistent comparison
                    const multipliers: Record<string, number> = {
                        B: 1,
                        BYTES: 1,
                        KB: 1024,
                        MB: 1024 * 1024,
                        GB: 1024 * 1024 * 1024,
                    };

                    const multiplier = multipliers[unit] || 1024; // Default to KB multiplier
                    return numericValue * multiplier;
                };

                const aSize = parseSize(aValue as string | number);
                const bSize = parseSize(bValue as string | number);

                // Handle invalid values (-1) - put them at the end
                if (aSize === -1 && bSize === -1) return 0;
                if (aSize === -1) return 1; // a goes to end
                if (bSize === -1) return -1; // b goes to end

                if (sortDirection === 'asc') {
                    return aSize - bSize;
                } else {
                    return bSize - aSize;
                }
            }

            // Handle different data types
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
            }

            // Handle strings
            const aStr = String(aValue).toLowerCase();
            const bStr = String(bValue).toLowerCase();

            if (sortDirection === 'asc') {
                return aStr.localeCompare(bStr);
            } else {
                return bStr.localeCompare(aStr);
            }
        });
    }, [rows, sortColumn, sortDirection]);

    // Reset page when rows or sorting changes (only for client-side pagination)
    useEffect(() => {
        if (!serverSide) {
            setInternalPage(0);
        }
    }, [rows, sortColumn, sortDirection, serverSide]);

    // Only show the sticky actions-column shadow when the table actually overflows horizontally,
    // otherwise it renders as a stray vertical line even when there's nothing to scroll.
    useEffect(() => {
        const container = internalScrollRef.current;
        if (!container) {
            return;
        }

        const checkOverflow = () => {
            setIsHorizontallyScrollable(container.scrollWidth > container.clientWidth);
        };

        checkOverflow();

        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        const resizeObserver = new ResizeObserver(checkOverflow);
        resizeObserver.observe(container);

        return () => resizeObserver.disconnect();
    }, [rows, columns, isActions]);

    // Calculate paginated rows
    const paginatedRows = useMemo(() => {
        if (!isPagination) {
            return sortedRows;
        }
        // For server-side pagination, don't slice - show all rows passed (which should be current page)
        if (serverSide) {
            return sortedRows;
        }
        // For client-side pagination, if rowsPerPage is ALL_ROWS_VALUE (All), show all rows
        if (rowsPerPage === ALL_ROWS_VALUE || rowsPerPage === -1) {
            return sortedRows;
        }
        // For client-side pagination, slice the rows
        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return sortedRows.slice(startIndex, endIndex);
    }, [sortedRows, page, rowsPerPage, isPagination, serverSide]);

    const handleChangePage = (_event: unknown, newPage: number) => {
        // Don't allow page changes when "All" is selected
        if (rowsPerPage === ALL_ROWS_VALUE || rowsPerPage === -1) {
            return;
        }
        if (serverSide) {
            // For server-side, just call the callback - parent will update the controlled page
            if (onPageChange) {
                onPageChange(newPage + 1, rowsPerPage); // Convert to 1-based page for API
            }
        } else {
            // For client-side, update internal state
            setInternalPage(newPage);
        }
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = event.target.value;
        // Handle both number strings and object values
        // Material-UI will pass the value from the object, so we check for -1 or the large number
        let newRowsPerPage: number;
        const parsedValue = parseInt(value, 10);

        // Check if it's "All" option (could be string "All", number ALL_ROWS_VALUE, or -1)
        if (
            value === '-1' ||
            value === 'All' ||
            parsedValue === ALL_ROWS_VALUE ||
            parsedValue === -1 ||
            value === String(ALL_ROWS_VALUE)
        ) {
            newRowsPerPage = ALL_ROWS_VALUE;
        } else if (!isNaN(parsedValue)) {
            newRowsPerPage = parsedValue;
        } else {
            // Fallback to default
            newRowsPerPage = 10;
        }

        if (serverSide) {
            // For server-side, just call the callback - parent will update the controlled values
            if (onPageChange) {
                // If "All" is selected, pass a large number or handle it in parent
                onPageChange(1, newRowsPerPage === ALL_ROWS_VALUE ? 999999 : newRowsPerPage); // Reset to page 1 with new limit
            }
        } else {
            // For client-side, update internal state
            setInternalRowsPerPage(newRowsPerPage);
            setInternalPage(0);
        }
    };

    const menuProps: Partial<MenuProps> = {
        anchorOrigin: {
            vertical: 'top',
            horizontal: 'left',
        },
        transformOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
        },
        PaperProps: {
            sx: {
                borderRadius: '8px',
                boxShadow: theme.shadows[1],
                '& .MuiButtonBase-root': {
                    ...theme.typography.body2,
                },
            },
        },
    };

    const handleToggleRow = (rowIndex: number) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(rowIndex)) {
            newExpanded.delete(rowIndex);
        } else {
            newExpanded.add(rowIndex);
        }
        setExpandedRows(newExpanded);
    };

    const renderCell = (colId: string, value: unknown) => {
        if (colId === 'autopayEnabled') {
            const isYes = String(value).toLowerCase() === 'yes';
            return (
                <CommonStatusChip
                    label={String(value)}
                    chipBgColor={isYes ? colors.successPreset[30] : colors.errorPreset[30]}
                    sx={{ minWidth: 60 }}
                />
            );
        }
        if (colId === 'status') {
            // If value is already a React element, return it as-is
            if (React.isValidElement(value)) {
                return value;
            }
            // Handle N/A status with specific colors
            if (value === 'N/A') {
                return <CommonStatusChip label={value} chipBgColor={colors.greyPreset[80]} />;
            }
            // Support request list only: distinct light colors per status
            if (statusVariant === 'support') {
                const supportStatusColors: Record<string, { bg: string }> = {
                    Open: {
                        bg: colors.bluePreset[30],
                    },
                    'In Progress': {
                        bg: colors.warningPreset[70],
                    },
                    Closed: {
                        bg: colors.successPreset[30],
                    },
                };
                const chipColors = supportStatusColors[value as string] ?? {
                    bg: colors.greyPreset[80],
                };
                return <CommonStatusChip label={String(value)} chipBgColor={chipColors.bg} />;
            }
            return (
                <CommonStatusChip
                    label={String(value)}
                    chipBgColor={value === 'Completed' ? colors.successPreset[30] : colors.bluePreset[30]}
                    sx={{ minWidth: 80 }}
                />
            );
        }
        if (colId === 'subject' || colId === 'requestType') {
            return (
                <Tooltip title={String(value || '')} arrow placement="bottom">
                    <Box className={colId === 'subject' ? 'tooltip-text-ellipsis-wide' : 'tooltip-text-ellipsis'}>
                        {String(value)}
                    </Box>
                </Tooltip>
            );
        }
        return value;
    };

    const renderSortIcon = (columnId: string) => {
        const column = columns.find(col => col.id === columnId);

        // Only show icons for sortable columns
        if (!column?.sortable) {
            return null;
        }

        // Show active sort icon when this column is sorted
        if (sortColumn === columnId && sortDirection) {
            return sortDirection === 'asc' ? (
                <span
                    className="icon-upward-arrow"
                    style={{ fontSize: 20, marginLeft: 0.5, color: colors.fullBlack, fontWeight: 600 }}
                />
            ) : (
                <span
                    className="icon-downward-sort"
                    style={{ fontSize: 20, marginLeft: 0.5, color: colors.fullBlack, fontWeight: 600 }}
                />
            );
        }

        // default icon
        return (
            <span
                className="icon-upward-arrow"
                style={{ fontSize: 20, marginLeft: 0.5, color: colors.fullBlack, fontWeight: 600 }}
            />
        );
    };

    const renderFilterIcon = (columnId: string) => {
        const column = columns.find(col => col.id === columnId) as Column | undefined;
        if (!column?.filterOptions?.length || !onFilterChange) return null;
        return (
            <IconButton
                size="small"
                aria-label="Filter"
                onClick={e => {
                    e.stopPropagation();
                    setFilterAnchorEl(e.currentTarget);
                    setFilterColumnId(columnId);
                }}
                sx={{
                    padding: 0.25,
                    marginLeft: 0.5,
                    '&:hover': { backgroundColor: colors.greyPreset[90] },
                }}
            >
                <span
                    className="icon-filter"
                    style={{
                        fontSize: 20,
                        color: colors.fullBlack,
                        fontWeight: 600,
                    }}
                />
            </IconButton>
        );
    };

    const handleCloseFilterMenu = () => {
        setFilterAnchorEl(null);
        setFilterColumnId(null);
    };

    const handleFilterSelect = (columnId: string, value: string | null) => {
        onFilterChange?.(columnId, value);
        handleCloseFilterMenu();
    };

    const filterColumn = filterColumnId ? columns.find(col => col.id === filterColumnId) : null;
    const filterMenuOptions = filterColumn?.filterOptions;

    // Add Actions column if isActions is true
    const tableColumns = useMemo(() => {
        if (isActions) {
            return [
                ...columns,
                {
                    id: 'actions',
                    label: actionsLabel,
                    align: 'left' as const,
                    sortable: false,
                },
            ];
        }
        return columns;
    }, [columns, isActions, actionsLabel]);

    const renderActions = (row: Record<string, unknown>, actualRowIndex: number) => {
        if (!isActions || !actions || actions.length === 0) {
            return null;
        }

        const visibleActions = actions.filter((action, idx) => {
            if (idx >= 3) return false;
            if (action.visible && !action.visible(row, actualRowIndex)) return false;
            return true;
        });

        return (
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'start', alignItems: 'center' }}>
                {visibleActions.map((action, index) => {
                    const actionButton = (
                        <IconButton
                            key={index}
                            size="small"
                            onClick={() => action.onClick(row, actualRowIndex)}
                            disabled={action.disabled}
                            sx={{
                                padding: 0.75,
                                '&:hover': {
                                    backgroundColor: colors.greyPreset[90],
                                },
                                '&.Mui-disabled': {
                                    opacity: 0.5,
                                },
                            }}
                        >
                            <span
                                className={action.icon}
                                style={{
                                    fontSize: 18,
                                    fontWeight: 500,
                                    color: action.disabled
                                        ? colors.greyPreset[30]
                                        : action.iconColor || colors.fullBlack,
                                }}
                            />
                        </IconButton>
                    );

                    return action.tooltip ? (
                        <Tooltip key={index} title={action.tooltip}>
                            {actionButton}
                        </Tooltip>
                    ) : (
                        actionButton
                    );
                })}
            </Box>
        );
    };

    return (
        <TableContainerWrapper sx={{ ...sx, border: tableBorder }} className={className}>
            <Box
                ref={setScrollContainerRefs}
                sx={{
                    maxHeight: stickyHeader ? maxHeight : 'none',
                    overflow: stickyHeader ? 'auto' : 'visible',
                    overscrollBehaviorX: 'contain',
                }}
            >
                <Table stickyHeader={stickyHeader} sx={{ minWidth }} aria-label="dynamic table">
                    <TableHead>
                        <TableRow>
                            {enableAccordion && (
                                <TableCell
                                    component="th"
                                    scope="col"
                                    sx={{
                                        width: 50,
                                        ...(stickyHeader && {
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 3,
                                        }),
                                    }}
                                >
                                    <span
                                        style={{
                                            position: 'absolute',
                                            width: 1,
                                            height: 1,
                                            padding: 0,
                                            margin: -1,
                                            overflow: 'hidden',
                                            clip: 'rect(0,0,0,0)',
                                            whiteSpace: 'nowrap',
                                            border: 0,
                                        }}
                                    >
                                        Expand or collapse row
                                    </span>
                                </TableCell>
                            )}
                            {tableColumns.map(col => {
                                const headerText = (col.label && String(col.label).trim()) || col.id || '\u00A0';
                                return (
                                    <TableCell
                                        key={col.id}
                                        component="th"
                                        scope="col"
                                        align={col.align}
                                        sx={{
                                            cursor: col.sortable ? 'pointer' : 'default',
                                            userSelect: 'none',
                                            ...(col.minWidth != null && { minWidth: col.minWidth }),
                                            ...(stickyHeader && {
                                                position: 'sticky',
                                                top: 0,
                                                zIndex: 3,
                                            }),
                                            ...(col.id === 'actions' && {
                                                position: 'sticky',
                                                right: 0,
                                                zIndex: stickyHeader ? 4 : 2,
                                                bgcolor: colors.fullWhite,
                                                ...(isHorizontallyScrollable && !hideActionsShadow && {
                                                    boxShadow: `-8px 0 12px -6px ${colors.greyPreset[30]}99`,
                                                }),
                                            }),
                                        }}
                                        onClick={() => col.sortable && handleSort(col.id)}
                                    >
                                        <Box
                                            className="tableHeaderCell"
                                            component="span"
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            {headerText}
                                            {renderSortIcon(col.id)}
                                            {renderFilterIcon(col.id)}
                                        </Box>
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedRows.map((row, rowIndex) => {
                            const isExpanded = expandedRows.has(rowIndex);
                            const actualRowIndex =
                                isPagination && rowsPerPage !== ALL_ROWS_VALUE && rowsPerPage !== -1
                                    ? page * rowsPerPage + rowIndex
                                    : rowIndex;

                            const hasRowDetail = renderRowDetail?.(row, actualRowIndex) != null;
                            return (
                                <React.Fragment key={rowIndex}>
                                    <TableRow
                                        sx={{
                                            '&:last-child td, &:last-child th': { border: 0 },
                                            ...(hasRowDetail && {
                                                '& td, & th': { borderBottom: 'none !important' },
                                            }),
                                            ...(getRowSx ? getRowSx(row, actualRowIndex) : {}),
                                        }}
                                    >
                                        {enableAccordion && (
                                            <TableCell sx={{ width: 50 }}>
                                                {row?.hasAccordion !== false && (
                                                    <IconButton
                                                        size="small"
                                                        aria-label={
                                                            isExpanded
                                                                ? `Collapse row ${rowIndex + 1}`
                                                                : `Expand row ${rowIndex + 1}`
                                                        }
                                                        onClick={() => handleToggleRow(rowIndex)}
                                                        sx={{
                                                            padding: 0,
                                                        }}
                                                    >
                                                        <span
                                                            className="icon-arrow-drop-down"
                                                            style={{
                                                                fontSize: 24,
                                                                fontWeight: 600,
                                                                color: theme.palette.text.secondary,
                                                                transform: isExpanded
                                                                    ? 'rotate(180deg)'
                                                                    : 'rotate(0deg)',
                                                                transition: 'transform 0.3s ease',
                                                                display: 'inline-block',
                                                            }}
                                                        />
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                        )}
                                        {tableColumns.map(col => (
                                            <TableCell
                                                key={col.id}
                                                align={col.align}
                                                sx={{
                                                    ...(col.minWidth != null && { minWidth: col.minWidth }),
                                                    ...(col.id === 'actions' && {
                                                        position: 'sticky',
                                                        right: 0,
                                                        zIndex: 1,
                                                        bgcolor: colors.fullWhite,
                                                        ...(isHorizontallyScrollable && !hideActionsShadow && {
                                                            boxShadow: `-8px 0 12px -6px ${colors.greyPreset[30]}99`,
                                                        }),
                                                    }),
                                                }}
                                            >
                                                {
                                                    (col.id === 'actions'
                                                        ? (row.actions as React.ReactNode) ||
                                                          renderActions(row, actualRowIndex)
                                                        : renderCell(col.id, row[col.id])) as React.ReactNode
                                                }
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                    {enableAccordion && renderAccordionContent && (
                                        <TableRow>
                                            <TableCell
                                                style={{ padding: 0 }}
                                                colSpan={tableColumns.length + (enableAccordion ? 1 : 0)}
                                            >
                                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                    <Stack>{renderAccordionContent(row, actualRowIndex)}</Stack>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {hasRowDetail && (
                                        <TableRow>
                                            <TableCell
                                                colSpan={tableColumns.length + (enableAccordion ? 1 : 0)}
                                                sx={{ padding: '0px 16px 12px !important', verticalAlign: 'top' }}
                                            >
                                                {renderRowDetail(row, actualRowIndex)}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </Box>
            {isPagination && (
                <Box
                    pt={1.5}
                    sx={{
                        '& .MuiTablePagination-root': {
                            border: 'none',
                        },
                    }}
                >
                    <TablePagination
                        component="div"
                        count={serverSide && totalCount !== undefined ? totalCount : sortedRows.length}
                        page={rowsPerPage === ALL_ROWS_VALUE || rowsPerPage === -1 ? 0 : page}
                        onPageChange={handleChangePage}
                        rowsPerPage={
                            rowsPerPage === ALL_ROWS_VALUE || rowsPerPage === -1 ? ALL_ROWS_VALUE : rowsPerPage
                        }
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 25, 50, 100, { value: ALL_ROWS_VALUE, label: 'All' }]}
                        labelDisplayedRows={({ from, to, count }) =>
                            `${from} - ${to} of ${count !== -1 ? count : `more than ${to}`}`
                        }
                        SelectProps={{
                            MenuProps: menuProps,
                            renderValue: (selected: unknown) => {
                                const numValue = typeof selected === 'string' ? parseInt(selected, 10) : selected;
                                if (numValue === ALL_ROWS_VALUE || numValue === -1) {
                                    return 'All';
                                }
                                return String(selected || '');
                            },
                        }}
                        sx={{
                            borderTop: `1px solid ${colors.greyPreset[30]}`,
                            '& .MuiInputBase-root': {
                                '& .MuiSelect-select': {
                                    padding: '0px 24px 0px 0px',
                                },
                                '& .MuiSelect-select.MuiSelect-select': {
                                    '&:empty::before': {
                                        content: '""',
                                    },
                                },
                            },
                            '& .MuiTablePagination-toolbar': {
                                paddingLeft: 2,
                                paddingRight: 2,
                                minHeight: 'auto !important',
                                margin: 0,
                            },
                            '& .MuiTablePagination-selectLabel': {
                                ...theme.typography.body2,
                                color: colors.greyPreset[80],
                                fontWeight: 400,
                                margin: 0,
                            },
                            '& .MuiTablePagination-displayedRows': {
                                ...theme.typography.body2,
                                color: colors.greyPreset[80],
                                fontWeight: 400,
                                margin: 0,
                            },
                            '& .MuiTablePagination-select': {
                                ...theme.typography.body2,
                                color: colors.greyPreset[80],
                                fontWeight: 400,
                                marginRight: 0,
                            },
                            '& .MuiIconButton-root': {
                                color: colors.fullBlack,
                                padding: 0,
                                '&:hover': {
                                    backgroundColor: colors.greyPreset[90],
                                },
                                '&.Mui-disabled': {
                                    color: theme.palette.text.disabled,
                                },
                                ...((rowsPerPage === ALL_ROWS_VALUE || rowsPerPage === -1) && {
                                    '&:not(.Mui-disabled)': {
                                        pointerEvents: 'none',
                                        opacity: 0.5,
                                    },
                                }),
                            },
                        }}
                    />
                </Box>
            )}
            <Menu
                anchorEl={filterAnchorEl}
                open={Boolean(filterAnchorEl)}
                onClose={handleCloseFilterMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                    paper: {
                        sx: {
                            bgcolor: colors.fullWhite,
                            mt: 1,
                            borderRadius: theme.spacing(1),
                            boxShadow: theme.shadows[2],
                            border: `1px solid ${colors.greyPreset[30]}`,
                            overflow: 'hidden',
                            minWidth: 200,
                            '& .MuiList-root': {
                                gap: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                maxHeight: 260,
                                overflow: 'auto',
                            },
                        },
                    },
                }}
            >
                {filterColumnId && (
                    <MenuItem
                        onClick={() => handleFilterSelect(filterColumnId, null)}
                        sx={{
                            ...theme.typography.body2,
                            color: colors.fullBlack,
                            py: 1,
                            px: 2.5,
                            backgroundColor: !columnFilterValues[filterColumnId]
                                ? colors.primaryLight[5]
                                : 'transparent',
                            '&:hover': { backgroundColor: colors.primaryLight[5] },
                            '&.Mui-selected': {
                                backgroundColor: `${colors.primaryLight[5]} !important`,
                            },
                        }}
                    >
                        All
                    </MenuItem>
                )}
                {filterMenuOptions?.map(opt => (
                    <MenuItem
                        key={opt.value}
                        onClick={() => handleFilterSelect(filterColumnId!, opt.value)}
                        selected={columnFilterValues[filterColumnId!] === opt.value}
                        sx={{
                            ...theme.typography.body2,
                            color: colors.fullBlack,
                            py: 1,
                            px: 2.5,
                            backgroundColor:
                                columnFilterValues[filterColumnId!] === opt.value
                                    ? colors.primaryLight[5]
                                    : 'transparent',
                            '&:hover': { backgroundColor: colors.primaryLight[5] },
                            '&.Mui-selected': {
                                backgroundColor: `${colors.primaryLight[5]} !important`,
                            },
                        }}
                    >
                        {opt.label}
                    </MenuItem>
                ))}
            </Menu>
        </TableContainerWrapper>
    );
};

export default CommonTable;
